import React, { useState, useEffect, useRef } from "react";
import logo from "../image/travellog.jpeg";
import "./Navbar.css";
import { Link } from "react-router-dom";
import Marquee from "./Marquee";
import { useAuth } from "../../AuthContext";
import { LuUserCircle2 } from "react-icons/lu";
import { FaUserAltSlash } from "react-icons/fa";
import { FiUserPlus } from "react-icons/fi";
import { FaGlobeAmericas } from "react-icons/fa";
import { MdSchool } from "react-icons/md";
import { FaBriefcase } from "react-icons/fa";
import { FaHandsHelping } from "react-icons/fa";
import { FaStethoscope } from "react-icons/fa";
import { FaTooth } from "react-icons/fa";
import { FaUserNurse } from "react-icons/fa";
import { FaAmbulance } from "react-icons/fa";
import { FaLanguage } from "react-icons/fa";
import { FaHome } from "react-icons/fa";
import { FaPhone } from "react-icons/fa";
import { FaBars, FaTimes, FaChevronDown, FaPassport } from "react-icons/fa";

/* =====================================================
   MOBILE ACCORDION
   ===================================================== */
const MobAccordion = ({ label, icon: Icon, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`mob-accordion${open ? " is-open" : ""}`}>
      <button
        className="mob-accordion-btn"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
      >
        {Icon && <Icon className="mab-icon" />}
        <span>{label}</span>
        <FaChevronDown className={`mab-chev${open ? " rotated" : ""}`} />
      </button>
      <div className="mob-accordion-body">
        {children}
      </div>
    </div>
  );
};

/* =====================================================
   NAV DROPDOWN
   ===================================================== */
const NavDropdown = ({ label, icon: Icon, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="nav-dropdown" ref={ref}>
      <button
        className={`nav-dropdown-btn${open ? " is-open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        {Icon && <Icon className="nav-icon" />}
        {label}
        <FaChevronDown className={`nav-chevron${open ? " rotated" : ""}`} />
      </button>
      <div className={`nav-dd-menu${open ? " is-visible" : ""}`}>
        <div className="nav-dd-inner" onClick={() => setOpen(false)}>
          {children}
        </div>
      </div>
    </div>
  );
};

/* =====================================================
   MAIN NAVBAR
   ===================================================== */
const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);
  const logoutPage  = () => { logout(); closeMobile(); };

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <>
      {/* ══════════════════════
          TOP BAR (desktop only)
          ══════════════════════ */}
      <div className="topbar">
        <div className="topbar-inner">
          <div className="topbar-logo-container">
            <img src={logo} alt="TravelOG" className="topbar-logo" />
          </div>
          <div className="topbar-contact">
            <a href="tel:+919922514719" className="topbar-link">
              <FaPhone className="topbar-link-icon" /> +91 9922514719
            </a>
            <a href="tel:+917756853249" className="topbar-link">
              <FaPhone className="topbar-link-icon" /> +91 7756853249
            </a>
          </div>
        </div>
      </div>

      {/* ══════════════════════════
          DESKTOP TWO-LINE NAVBAR
          ══════════════════════════ */}
      <div className="two-line-navbar">

        {/* ── ROW 1 : Medical Careers ─────────────── */}
        <div className="navbar-first-line">
          <div className="nav-row">

            <Link to="/" className="nav-link-item home-link">
              <FaHome className="nav-icon" /> Home
            </Link>

            {/* Doctor Career */}
            <NavDropdown label="Doctor Career" icon={FaStethoscope}>
              <div className="nav-dd-label">Doctor Career</div>
              <Link to="/Doctor/Australia" className="nav-dd-item">🇦🇺 Australia</Link>
              <Link to="/Doctor/USA"       className="nav-dd-item">🇺🇸 USA</Link>
              <Link to="/Doctor/Germany"   className="nav-dd-item">🇩🇪 Germany</Link>
            </NavDropdown>

            {/* Dentist Career */}
            <NavDropdown label="Dentist Career" icon={FaTooth}>
              <div className="nav-dd-label">Dentist Career</div>
              <Link to="/Dentist/Australia" className="nav-dd-item">🇦🇺 Australia</Link>
              <Link to="/Dentist/USA"       className="nav-dd-item">🇺🇸 USA</Link>
              <Link to="/Dentist/Germany"   className="nav-dd-item">🇩🇪 Germany</Link>
            </NavDropdown>

            {/* Physiotherapy */}
            <NavDropdown label="Physiotherapy" icon={FaHandsHelping}>
              <div className="nav-dd-label">Physiotherapy</div>
              <Link to="/physio/Australia" className="nav-dd-item">🇦🇺 Australia</Link>
              <Link to="/physio/USA"       className="nav-dd-item">🇺🇸 USA</Link>
              <Link to="/physio/germany"   className="nav-dd-item">🇩🇪 Germany</Link>
            </NavDropdown>

            {/* Nurse Career */}
            <NavDropdown label="Nurse Career" icon={FaUserNurse}>
              <div className="nav-dd-label">Nurse Career</div>
              <Link to="/Nurse/Australia" className="nav-dd-item">🇦🇺 Australia</Link>
              <Link to="/Nurse/USA"       className="nav-dd-item">🇺🇸 USA</Link>
              <Link to="/Nurse/Germany"   className="nav-dd-item">🇩🇪 Germany</Link>
            </NavDropdown>

            {/* Paramedical */}
            <NavDropdown label="Paramedical" icon={FaAmbulance}>
              <div className="nav-dd-label">Paramedical</div>
              <Link to="/Para/Australia" className="nav-dd-item">🇦🇺 Australia</Link>
              <Link to="/Para/USA"       className="nav-dd-item">🇺🇸 USA</Link>
              <Link to="/Para/Germany"   className="nav-dd-item">🇩🇪 Germany</Link>
            </NavDropdown>

            {/* ✅ IMMIGRATION — fixed, no nested NavDropdown */}
            <NavDropdown label="Immigration" icon={FaPassport}>
              <div className="nav-dd-label">Immigration Destinations</div>
              <Link to="/immigration/australia"  className="nav-dd-item">🇦🇺 Immigration to Australia</Link>
              <Link to="/immigration/canada"     className="nav-dd-item">🇨🇦 Immigration to Canada</Link>
              <Link to="/immigration/newzealand" className="nav-dd-item">🇳🇿 Immigration to New Zealand</Link>
            </NavDropdown>

          </div>
        </div>

        {/* ── ROW 2 : Study, Work & More ──────────── */}
        <div className="navbar-second-line">
          <div className="nav-row">

            {/* Study Abroad */}
            <NavDropdown label="Study Abroad" icon={MdSchool}>
              <div className="nav-dd-label">Study Abroad</div>
              <Link to="/study/australia" className="nav-dd-item">🇦🇺 Australia</Link>
              <Link to="/study/usa"       className="nav-dd-item">🇺🇸 USA</Link>
              <Link to="/study/germany"   className="nav-dd-item">🇩🇪 Germany</Link>
            </NavDropdown>

            {/* International Work Experience */}
            <NavDropdown label="International Work Experience" icon={FaBriefcase}>
              <div className="nav-dd-label">Work Experience</div>
              <Link to="/workexperience/usa"    className="nav-dd-item nav-dd-item--sub">🇺🇸 Work Experience in USA</Link>
              <Link to="/workexperience/canada" className="nav-dd-item nav-dd-item--sub">🇨🇦 Work Experience in Canada</Link>
              <Link to="/workexperience/europe" className="nav-dd-item nav-dd-item--sub">🇪🇺 Work Experience in Europe</Link>
            </NavDropdown>

           <Link to="/internship/india" className="nav-link-item">
  <FaGlobeAmericas className="nav-icon" /> Internship in India
</Link>

            {/* Language Learning */}
            <NavDropdown label="Language Learning" icon={FaLanguage}>
              <div className="nav-dd-label">Language Programs</div>
              <Link to="/learning/germanlang" className="nav-dd-item">🇩🇪 German Language</Link>
              <Link to="/learning/toefllang"  className="nav-dd-item">📝 TOEFL · IELTS · OAT</Link>
            </NavDropdown>

            {/* Auth */}
            {isAuthenticated && user ? (
              <NavDropdown
                label={user.userName || user.userEmail || "User"}
                icon={LuUserCircle2}
              >
                <div className="nav-dd-label">
                  {user.userRole === "doctor"   ? "Doctor Account"   :
                   user.userRole === "hospital" ? "Hospital Account" : "Patient Account"}
                </div>
                <Link to="/" onClick={logoutPage} className="nav-dd-item logout-item">
                  <FaUserAltSlash className="di-icon" /> Logout
                </Link>
              </NavDropdown>
            ) : (
              <>
                <Link to="/login"    className="nav-link-item auth-link">
                  <LuUserCircle2 className="nav-icon" /> Login
                </Link>
                <Link to="/register" className="nav-link-item auth-link">
                  <FiUserPlus className="nav-icon" /> Register
                </Link>
              </>
            )}

            <Link to="/Contact" className="nav-link-item contact-link">
              <FaPhone className="nav-icon" /> Contact Us
            </Link>

          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          MOBILE SECTION
          ══════════════════════════════════════════ */}
      <div className="mobile-only">

        <div className="mobile-header">
          <img src={logo} alt="Logo" className="mobile-logo" />
          <button
            className={`hamburger-btn${mobileOpen ? " is-open" : ""}`}
            onClick={() => setMobileOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        <div
          className={`mob-overlay${mobileOpen ? " is-visible" : ""}`}
          onClick={closeMobile}
        />

        <div className={`mob-drawer${mobileOpen ? " is-open" : ""}`}>
          <div className="mob-drawer-inner">

            <Link to="/" className="mob-link home-mob" onClick={closeMobile}>
              <FaHome /> Home
            </Link>
            <Link to="/Contact" className="mob-link contact-mob" onClick={closeMobile}>
              <FaPhone /> Contact Us
            </Link>

            {/* ── Medical Careers ── */}
            <div className="mob-section-label">Medical Careers</div>

            <MobAccordion label="Doctor Career" icon={FaStethoscope}>
              <Link to="/Doctor/Australia" onClick={closeMobile}>🇦🇺 Australia</Link>
              <Link to="/Doctor/USA"       onClick={closeMobile}>🇺🇸 USA</Link>
              <Link to="/Doctor/Germany"   onClick={closeMobile}>🇩🇪 Germany</Link>
            </MobAccordion>

            <MobAccordion label="Dentist Career" icon={FaTooth}>
              <Link to="/Dentist/Australia" onClick={closeMobile}>🇦🇺 Australia</Link>
              <Link to="/Dentist/USA"       onClick={closeMobile}>🇺🇸 USA</Link>
              <Link to="/Dentist/Germany"   onClick={closeMobile}>🇩🇪 Germany</Link>
            </MobAccordion>

            <MobAccordion label="Physiotherapy" icon={FaHandsHelping}>
              <Link to="/physio/Australia" onClick={closeMobile}>🇦🇺 Australia</Link>
              <Link to="/physio/USA"       onClick={closeMobile}>🇺🇸 USA</Link>
              <Link to="/physio/germany"   onClick={closeMobile}>🇩🇪 Germany</Link>
            </MobAccordion>

            <MobAccordion label="Nurse Career" icon={FaUserNurse}>
              <Link to="/Nurse/Australia" onClick={closeMobile}>🇦🇺 Australia</Link>
              <Link to="/Nurse/USA"       onClick={closeMobile}>🇺🇸 USA</Link>
              <Link to="/Nurse/Germany"   onClick={closeMobile}>🇩🇪 Germany</Link>
            </MobAccordion>

            <MobAccordion label="Paramedical" icon={FaAmbulance}>
              <Link to="/Para/Australia" onClick={closeMobile}>🇦🇺 Australia</Link>
              <Link to="/Para/USA"       onClick={closeMobile}>🇺🇸 USA</Link>
              <Link to="/Para/Germany"   onClick={closeMobile}>🇩🇪 Germany</Link>
            </MobAccordion>

            {/* ✅ IMMIGRATION — fixed, 3 countries only */}
            <MobAccordion label="Immigration" icon={FaPassport}>
              <Link to="/immigration/australia"  onClick={closeMobile}>🇦🇺 Immigration to Australia</Link>
              <Link to="/immigration/canada"     onClick={closeMobile}>🇨🇦 Immigration to Canada</Link>
              <Link to="/immigration/newzealand" onClick={closeMobile}>🇳🇿 Immigration to New Zealand</Link>
            </MobAccordion>

            {/* ── Global Opportunities ── */}
            <div className="mob-section-label">Global Opportunities</div>

            <MobAccordion label="Study Abroad" icon={MdSchool}>
              <Link to="/study/australia" onClick={closeMobile}>🇦🇺 Australia</Link>
              <Link to="/study/usa"       onClick={closeMobile}>🇺🇸 USA</Link>
              <Link to="/study/germany"   onClick={closeMobile}>🇩🇪 Germany</Link>
            </MobAccordion>

            <MobAccordion label="International Work Experience" icon={FaBriefcase}>
              <Link to="/workexperience/usa"    onClick={closeMobile}>🇺🇸 Work Experience in USA</Link>
              <Link to="/workexperience/canada" onClick={closeMobile}>🇨🇦 Work Experience in Canada</Link>
              <Link to="/workexperience/europe" onClick={closeMobile}>🇪🇺 Work Experience in Europe</Link>
            </MobAccordion>

          <Link to="/internship/india" className="mob-link" onClick={closeMobile}>
  <FaGlobeAmericas /> Internship in India
</Link>

            <MobAccordion label="Language Learning" icon={FaLanguage}>
              <Link to="/learning/germanlang" onClick={closeMobile}>🇩🇪 German Language</Link>
              <Link to="/learning/toefllang"  onClick={closeMobile}>📝 TOEFL · IELTS · OAT</Link>
            </MobAccordion>

            {/* ── Account ── */}
            <div className="mob-section-label">Account</div>

            {isAuthenticated && user ? (
              <button className="mob-link logout-mob" onClick={logoutPage}>
                <FaUserAltSlash /> Logout
              </button>
            ) : (
              <>
                <Link to="/login"    className="mob-link" onClick={closeMobile}>
                  <LuUserCircle2 /> Login
                </Link>
                <Link to="/register" className="mob-link" onClick={closeMobile}>
                  <FiUserPlus /> Register
                </Link>
              </>
            )}

          </div>
        </div>
      </div>

      <Marquee />
    </>
  );
};

export default Navbar;